import { useEffect, useRef } from "react";
import { fetchPincodeDetailsAction } from "../actions/pincodeAction";

/**
 * usePincodeAutofill
 *
 * Watches a pincode value and auto-fills city + state when it reaches 6 digits.
 *
 * Behaviour:
 *  - Skips the very first call after `skipOnMount` is true (prevents overwriting
 *    city/state that were already prefilled from an API response).
 *  - Calls `onSuccess({ city, state })` when the pincode resolves.
 *  - Calls `onError(message)` when the pincode is invalid / API fails.
 *  - Calls `onClear()` when the pincode is cleared / incomplete (< 6 digits).
 *
 * @param {object} options
 * @param {string}   options.pincode       - Current pincode value (raw, may contain non-digits)
 * @param {boolean}  options.skipOnMount   - Skip the first valid 6-digit pincode hit
 *                                           (use when the form is pre-filled from an API)
 * @param {function} options.onSuccess     - ({ city, state }) => void
 * @param {function} [options.onError]     - (message: string) => void
 * @param {function} [options.onClear]     - () => void  — called when pin < 6 digits
 */
const usePincodeAutofill = ({
  pincode,
  skipOnMount = false,
  onSuccess,
  onError,
  onClear,
}) => {
  // Tracks the last pincode we already fetched — avoids duplicate API calls
  const lastFetchedPin = useRef(null);
  // Tracks whether we've seen the first valid pin (used for skipOnMount)
  const isFirstValidPin = useRef(true);

  useEffect(() => {
    const pin = String(pincode || "").replace(/\D/g, "");

    // Incomplete — notify caller to clear city/state if needed
    if (pin.length < 6) {
      lastFetchedPin.current = null;
      onClear?.();
      return;
    }

    // Already fetched this exact pin — nothing to do
    if (pin === lastFetchedPin.current) return;

    // Skip the very first hit when the form is pre-filled from an API
    if (skipOnMount && isFirstValidPin.current) {
      isFirstValidPin.current = false;
      lastFetchedPin.current = pin;
      return;
    }

    isFirstValidPin.current = false;
    lastFetchedPin.current = pin;

    fetchPincodeDetailsAction(pin).then((result) => {
      if (result.success) {
        onSuccess({ city: result.city, state: result.state });
      } else {
        onError?.(
          result.error ||
            "Invalid pin code, please enter the correct pin code.",
        );
      }
    });
  }, [pincode]); // eslint-disable-line react-hooks/exhaustive-deps
};

export default usePincodeAutofill;
